import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, ChevronDown, Check, Eraser } from 'lucide-react';
import { fetchNotes } from '@/hooks/useNotes';
import { cn } from '@/lib/utils';
import type { NoteSnippet } from '@uniticket/shared';

// « Insérer une réponse type » — le geste utile des réponses partagées (demande user 2026-09-10).
//
// POURQUOI ICI ET PAS SUR LA PAGE DES NOTES : une réponse type sert à RÉPONDRE. La poser sur l'onglet
// obligerait à un aller-retour onglet → ticket → coller, c'est-à-dire exactement la corvée que la
// feature devait supprimer. La page garde « Copier » ; l'insertion vit là où la question s'écrit.
//
// ⚠️ ON APPEND DU TEXTE BRUT, ON NE TOUCHE PAS AUX MENTIONS. `MentionTextarea` tient sa liste de
// personnes dans un état séparé (`picked`), résolu à l'envoi par `finalizeMentions` : ajouter du texte
// via `onChange` ne peut donc pas corrompre cet état — à condition de ne jamais réécrire `picked` ici.
// Le seul endroit qui y touche est « Vider », et c'est délibéré (voir `onClear`).
export function SnippetInsert({ onInsert, onClear, currentText, disabled }: {
  onInsert: (text: string) => void;
  /** Vide la zone de saisie ET les mentions déjà choisies : des mentions accrochées à un message
   *  effacé partiraient quand même à l'envoi. Le bouton n'apparaît que s'il y a quelque chose à vider. */
  onClear: () => void;
  /** Le contenu actuel de la zone — sert à savoir ce qui est DÉJÀ inséré. */
  currentText: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation('tickets');
  const [open, setOpen] = useState(false);
  const [snippets, setSnippets] = useState<NoteSnippet[] | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Chargement PARESSEUX, à la première ouverture : la fenêtre de validation est déjà l'écran le plus
  // lourd de l'app, et la plupart des dossiers se traitent sans réponse type.
  useEffect(() => {
    if (!open || snippets != null) return;
    let alive = true;
    fetchNotes()
      .then((res) => { if (alive) setSnippets(res.data.snippets); })
      .catch(() => { if (alive) setSnippets([]); });
    return () => { alive = false; };
  }, [open, snippets]);

  // Fermeture au clic à côté et à Échap. Sans ça la liste restait ouverte et masquait le formulaire —
  // on capture en phase de CAPTURE pour que la fermeture arrive même si un enfant arrête la propagation.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /** Déjà présent dans la zone ? On compare le CORPS, pas le titre : c'est le corps qui est inséré.
   *  Un corps vide n'est jamais « déjà là » — `includes('')` est toujours vrai, et ça aurait grisé
   *  toutes les réponses vides. */
  const already = (body: string) => {
    const b = body.trim();
    return b.length > 0 && currentText.includes(b);
  };

  return (
    <div className="mb-2 flex items-center gap-2">
      <div className="relative" ref={boxRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          title={t('notes.insertTitle')}
          className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:text-brand-rose disabled:opacity-40"
        >
          <ClipboardList className="h-3.5 w-3.5" />
          {t('notes.insert')}
          <ChevronDown className="h-3 w-3" />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 max-h-64 w-80 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
            {snippets == null ? (
              <p className="px-3 py-2 text-xs text-slate-400">…</p>
            ) : snippets.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400">{t('notes.sharedEmpty')}</p>
            ) : (
              snippets.map((s) => {
                const dup = already(s.body);
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={dup}
                    // Insérer deux fois la même réponse empilait deux copies identiques. On l'empêche
                    // AVANT le clic plutôt que d'avaler le geste : un bouton qui ne fait rien se lit
                    // comme une panne, un bouton grisé qui dit « déjà inséré » s'explique tout seul.
                    onClick={() => { onInsert(s.body); setOpen(false); }}
                    className={cn(
                      'block w-full px-3 py-2 text-left',
                      dup ? 'cursor-not-allowed bg-slate-50' : 'hover:bg-slate-50',
                    )}
                  >
                    <span className={cn('flex items-center gap-1 text-xs font-semibold',
                      dup ? 'text-slate-400' : 'text-slate-800')}>
                      {dup && <Check className="h-3 w-3 text-emerald-600" />}
                      {s.title}
                      {dup && <span className="ml-1 font-normal">· {t('notes.alreadyInserted')}</span>}
                    </span>
                    <span className="block truncate text-[11px] text-slate-500">{s.body}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      {currentText.trim().length > 0 && (
        <button
          type="button"
          disabled={disabled}
          onClick={onClear}
          title={t('notes.clearTitle')}
          className="inline-flex items-center gap-1.5 rounded border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:text-red-600 disabled:opacity-40"
        >
          <Eraser className="h-3.5 w-3.5" />
          {t('notes.clear')}
        </button>
      )}
    </div>
  );
}
